import React, { useEffect, useState } from 'react';
import { ApiButton, Box, Button, ErrorInfo } from 'web-library';
import { useTranslation } from 'react-i18next';
import { useForm } from 'react-hook-form';
import { MutationStatus } from 'react-query';
import { joiResolver } from '@hookform/resolvers/joi';

import { useDispatch, useSelector } from 'react-redux';
import {
  AdminProfileFormData,
  AdminProfileFormDefaultValues,
  AdminProfileIsDisableInputs,
} from '../../../../models';
import { useStyles } from './styles';
import AdminProfilePersonalParticulars from './AdminProfilePersonalParticulars';
import { selectFormEditMode, UpdateFormEdit } from '../../../../store/formSlice';
import AdminProfileContactDetails from './AdminProfileContactDetails';
import { useGetDocumentUploadStatus } from '../../../../api-refactored/document/useGetDocumentUploadStatus';
import { FileUploadState } from '../../../../models/File';
import ProfilePicture from './HelperComponents/ProfilePicture';
import { acceptableProfilePhotoExtension, uploadFile } from '../../../../utils/FileUtils';
import { usePostUploadPresignedUrl } from '../../../../api-refactored/user/usePostUploadPresignedUrl';
import { usePutUploadToS3 } from '../../../../api-refactored/s3-upload/usePutUploadFileToS3';
import adminSchema from '../../schema/adminSchema';

export interface AdminProfileFormProps {
  isHideEditButton: boolean;
  onCallHandleSubmit: (formData: AdminProfileFormData) => void;
  formDefaultValues?: AdminProfileFormDefaultValues;
  isDisableInputs?: AdminProfileIsDisableInputs;
  isDisableValidationForDisabledInputs?: boolean;
  isHideExtraFormInfo?: boolean;
  ApiButtonStatus?: MutationStatus;
  submitError?: string;
  isCreate?: boolean;
  refetchProfileData?: any;
}

const AdminProfileForm = ({
  isHideEditButton,
  onCallHandleSubmit,
  formDefaultValues = undefined,
  isDisableInputs = undefined,
  isDisableValidationForDisabledInputs = false,
  isHideExtraFormInfo = false,
  ApiButtonStatus = 'idle',
  submitError = '',
  isCreate = false,
  refetchProfileData,
}: AdminProfileFormProps) => {
  const { classes } = useStyles();
  const { t } = useTranslation();
  const [fileUrl, setFileUrl] = useState<string | undefined>(formDefaultValues?.photoUrl);
  const [fileUuid, setFileUuid] = useState<string | undefined>(formDefaultValues?.photoUUid);
  const [fileUploadErrorInd, setFileUploadErrorInd] = useState(false);
  const [fileCompleteProcessingInd, setFileCompleteProcessingInd] = useState(false); // use to disable getFileStatus call before PostpresignedUrl
  const [uploadedFiles, setUploadedFiles] = useState<File[]>([]);
  const [fileProcessingInd, setFileProcessingInd] = useState(false);
  const [loading, setLoading] = useState(false);
  const [fileStatus, setFileStatus] = useState(FileUploadState.IDLE);
  const [fileUploadError, setFileUploadError] = useState('');
  const [fileUploadStatus, setFileUploadStatus] = useState('');
  const formHook = useForm<AdminProfileFormData>({
    mode: 'onSubmit',
    resolver: joiResolver(adminSchema(isDisableValidationForDisabledInputs ? isDisableInputs : {})),
    // resolver: async (data, context, options) => {
    //   // you can debug your validation schema here
    //   // eslint-disable-next-line no-console
    //   console.log(
    //     'validation result',
    //     await joiResolver(adminSchema(isDisableValidationForDisabledInputs ? isDisableInputs : {}))(
    //       data,
    //       context,
    //       options
    //     )
    //   );
    //   return joiResolver(adminSchema(isDisableValidationForDisabledInputs ? isDisableInputs : {}))(
    //     data,
    //     context,
    //     options
    //   );
    // },
    defaultValues: formDefaultValues,
  });

  const uploadMutation = usePutUploadToS3();

  // TODO add call to get adminProfileEditPermission (after new implementation)

  useEffect(
    () => {
      if (formDefaultValues) {
        formHook.reset(formDefaultValues);
        setFileUrl(formDefaultValues?.photoUrl);
        setFileUuid(formDefaultValues?.photoUUid);
      }
    },
    /* eslint-disable-next-line react-hooks/exhaustive-deps */
    [formDefaultValues]
  );

  const { mutate: getUploadPresignedUrlMutation } = usePostUploadPresignedUrl({
    onSuccess: (res) => {
      setFileUuid(res.key);
      setFileCompleteProcessingInd(true);
      uploadFile(res, uploadedFiles, uploadMutation.mutate);
    },
  });

  useEffect(() => {
    if (uploadedFiles[0]) {
      setFileUploadStatus(t('admin->>CreateOpsAdminForm.Uploading...'));
      setFileCompleteProcessingInd(false);
      getUploadPresignedUrlMutation({ uuid: '', fileName: uploadedFiles[0].name });
    }
  }, [getUploadPresignedUrlMutation, t, uploadedFiles]);

  const getDocumentUploadStatus = useGetDocumentUploadStatus({
    fileKey: fileUuid || '',
    enabled: !!fileUuid && fileCompleteProcessingInd && fileProcessingInd, // TODO: add the loading condition
    refetchInterval: 5000,
    onSuccess: (getDocumentData) => {
      setFileStatus(getDocumentData.fileStatus);
      setFileUploadStatus(t('admin->>CreateOpsAdminForm.Processing...'));
    },
  });

  useEffect(() => {
    if (fileStatus === FileUploadState.SUCCESS && fileUuid) {
      setFileProcessingInd(false);
      setFileStatus(FileUploadState.IDLE);
      setFileUrl(getDocumentUploadStatus.data?.url);
      setFileCompleteProcessingInd(false);
      setUploadedFiles([]);
      setLoading(false);
      setFileUploadErrorInd(false);
    } else if (fileStatus === FileUploadState.FAILED || fileStatus === FileUploadState.TIMEOUT) {
      setFileUploadError(t('admin->>CreateOpsAdminForm.Failed to upload the file.'));
      setFileStatus(FileUploadState.IDLE);
      setFileProcessingInd(false);
      setFileCompleteProcessingInd(false);
      setFileUploadErrorInd(true);
      setLoading(false);
      setUploadedFiles([]);
    }
  }, [fileStatus, fileUuid, getDocumentUploadStatus.data?.url, t]);

  const editMode = useSelector(selectFormEditMode);

  const dispatch = useDispatch();
  const { handleSubmit, reset } = formHook;

  const updateFormState = () => {
    reset();
    refetchProfileData();
    setFileUrl(formDefaultValues?.photoUrl);
    dispatch(UpdateFormEdit(!editMode));
  };

  return (
    <Box className={classes.mainContainer}>
      <form
        onSubmit={handleSubmit((formData) => {
          const request: AdminProfileFormData = {
            ...formData,
            photoUUid: fileUuid,
          };
          onCallHandleSubmit(request);
        })}
        className={classes.responsiveFlexContainer}
      >
        <ProfilePicture
          photoUrl={fileUrl}
          photoUuid={fileUuid}
          isEditable={isCreate ? true : editMode}
          personnelType={undefined}
          acceptableExtension={acceptableProfilePhotoExtension}
          fileUploadErrorInd={fileUploadErrorInd}
          loading={loading}
          statutoryName={formDefaultValues?.statutoryName}
          setFileProcessingInd={setFileProcessingInd}
          setFileUploadErrorInd={setFileUploadErrorInd}
          setUploadedFiles={setUploadedFiles}
          updateFormState={updateFormState}
          setFileUuid={setFileUuid}
          setLoading={setLoading}
          changeProfileText={t('admin->>CreateOpsAdminForm.Change Profile Picture')}
          createNewProfileInd={false}
          isPersonnelProfileInd={false}
          fileUploadError={fileUploadError}
          setFileUploadError={setFileUploadError}
          fileUploadStatus={fileUploadStatus}
          setFileUrl={setFileUrl}
        />

        <hr className={classes.divider} />

        <AdminProfilePersonalParticulars
          isEditState={isCreate ? true : editMode}
          formHook={formHook}
          isDisableInputs={isDisableInputs}
          isHideExtraFormInfo={isHideExtraFormInfo}
          formDefaultValues={formDefaultValues}
          isProfileUploading={loading}
        />

        <hr className={classes.divider} />

        <AdminProfileContactDetails
          isEditState={isCreate ? true : editMode}
          formHook={formHook}
          isDisableInputs={isDisableInputs}
          isHideExtraFormInfo={isHideExtraFormInfo}
          formDefaultValues={formDefaultValues}
          isProfileUploading={loading}
        />

        {(isCreate || editMode) && (
          <>
            <Box className={classes.submitButtonContainer}>
              {!isHideEditButton && (
                <Button
                  id="button-candidateProfile-cancel"
                  className={classes.backButton}
                  variant="outlined"
                  onClick={updateFormState}
                  disabled={loading}
                >
                  {t('profile->>CreateCandidateForm.Cancel')}
                </Button>
              )}

              <ApiButton
                id="button-candidateProfile-submit"
                status={ApiButtonStatus}
                type="submit"
                value="Submit"
                disabled={loading}
              >
                {t('profile->>CreateCandidateForm.Submit')}
              </ApiButton>
            </Box>

            <ErrorInfo label={submitError || ''} />
          </>
        )}
      </form>
    </Box>
  );
};
export default AdminProfileForm;
